import gql from 'graphql-tag';
import { useMutation, type MutationHookOptions } from '@apollo/react-hooks';
import { createAtlAttributionHeader } from '../../utils';
import { type ProductContextKey } from './lib/realtime';
import type { DocumentNode } from 'graphql';
import { commitMutation, graphql, ReactRelayContext } from 'react-relay';
import { fg } from '@atlaskit/platform-feature-flags';
import type { usePublishRealtimeChannelMutation } from './__generated__/usePublishRealtimeChannelMutation.graphql';
import { useContext } from 'react';

interface MutationVariables {
	context?: string;
	installationId: string;
	isGlobal?: boolean;
	name: string;
	payload: string;
	token?: string;
}

interface MutationData {
	ecosystem: {
		publishRealtimeChannel?: {
			eventId: string;
			eventTimestamp: string;
		};
	};
}

export type PublishRealtimeChannelPayload = {
	data: {
		channelName: string;
		eventPayload: string | Record<string, unknown>;
		isGlobal?: boolean;
		options?: {
			contextOverrides?: ProductContextKey[];
			token?: string;
		};
	};
};

export type PublishRealtimeChannel = (
	payload: PublishRealtimeChannelPayload,
	contextToken?: string,
) => Promise<{ eventId: string; eventTimestamp: string } | undefined>;

export const publishRealtimeChannel: DocumentNode = gql`
	mutation publishRealtimeChannel(
		$installationId: ID!
		$name: String!
		$payload: String!
		$context: String
		$isGlobal: Boolean
		$token: String
	) {
		ecosystem {
			publishRealtimeChannel(
				installationId: $installationId
				name: $name
				payload: $payload
				context: $context
				isGlobal: $isGlobal
				token: $token
			) {
				eventId
				eventTimestamp
			}
		}
	}
`;

export const usePublishRealtimeChannel = (
	installationId: string,
	context: string,
	options?: MutationHookOptions<MutationData, MutationVariables>,
) => {
	const relayContext = useContext(ReactRelayContext);
	const relayEnvironment = relayContext?.environment;

	const [apolloMutationFunction] = useMutation<MutationData, MutationVariables>(
		publishRealtimeChannel,
		options,
	);

	return async (
		payload: PublishRealtimeChannelPayload,
		contextToken?: string,
	): Promise<
		| {
				eventId: string;
				eventTimestamp: string;
		  }
		| undefined
	> => {
		const { channelName, isGlobal, options } = payload.data;

		const { token, contextOverrides } = options || {};

		const eventPayload = JSON.stringify(payload.data.eventPayload);
		const channelContext = contextOverrides
			? JSON.stringify({
					context,
					contextOverrides,
				})
			: context;

		if (relayEnvironment) {
			if (fg('forge-ui-add-relay-publish-realtime-mutation')) {
				return new Promise<{ eventId: string; eventTimestamp: string } | undefined>(
					(resolve, reject) => {
						commitMutation<usePublishRealtimeChannelMutation>(relayEnvironment, {
							mutation: graphql`
								mutation usePublishRealtimeChannelMutation(
									$installationId: ID!
									$name: String!
									$payload: String!
									$context: String
									$isGlobal: Boolean
									$token: String
								) {
									ecosystem {
										publishRealtimeChannel(
											installationId: $installationId
											name: $name
											payload: $payload
											context: $context
											isGlobal: $isGlobal
											token: $token
										) {
											eventId
											eventTimestamp
										}
									}
								}
							`,
							variables: {
								installationId,
								name: channelName,
								payload: eventPayload,
								context: channelContext,
								isGlobal: isGlobal ?? false,
								token,
							},
							cacheConfig: {
								metadata: {
									headers: {
										'atl-attribution': createAtlAttributionHeader(),
										...(contextToken ? { 'x-forge-context-token': contextToken } : {}),
									},
								},
							},
							onCompleted: (data, errors) => {
								if (errors) {
									reject(errors);
									return;
								}

								if (data?.ecosystem?.publishRealtimeChannel) {
									const { eventId, eventTimestamp } = data.ecosystem.publishRealtimeChannel;
									if (eventId && eventTimestamp) {
										resolve({ eventId, eventTimestamp });
									} else {
										resolve(undefined);
									}
								} else {
									resolve(undefined);
								}
							},
							onError: reject,
						});
					},
				);
			}
		}

		const { data } = await apolloMutationFunction({
			variables: {
				installationId,
				name: channelName,
				context: channelContext,
				payload: eventPayload,
				isGlobal: isGlobal ?? false,
				token,
			},
			context: {
				headers: {
					'atl-attribution': createAtlAttributionHeader(),
					...(contextToken ? { 'x-forge-context-token': contextToken } : {}),
				},
			},
		});

		if (data?.ecosystem.publishRealtimeChannel) {
			const { eventId, eventTimestamp } = data.ecosystem.publishRealtimeChannel;

			return {
				eventId,
				eventTimestamp,
			};
		}
	};
};
